import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { SyncRun } from '@octave/contracts';
import {
  formatDuration,
  formatExact,
  formatNightLabel,
  formatRelative,
  formatTime,
  scopeLabel,
  type HistoryItem,
  type NightGroup,
} from '@/lib/sync-monitoring';
import { ChevronRight, MoonStar } from 'lucide-react';
import { SyncKindBadge, SyncStatusBadge } from './SyncBadges';

function RunsTable({ runs }: { runs: SyncRun[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-1">Heure</TableHead>
          <TableHead>Organisation</TableHead>
          <TableHead className="w-1">Type</TableHead>
          <TableHead className="text-right">Maj</TableHead>
          <TableHead className="text-right">Soft-delete</TableHead>
          <TableHead className="text-right">Erreurs</TableHead>
          <TableHead className="text-right">Durée</TableHead>
          <TableHead className="w-1">Statut</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {runs.map((run) => (
          <TableRow key={run.id}>
            <TableCell className="tabular-nums text-slate-500">
              {formatTime(run.startedAt)}
            </TableCell>
            <TableCell>
              <div className="font-medium">{run.organizationName ?? '—'}</div>
              {run.errorMessage && (
                <div
                  className="max-w-xs truncate text-xs text-rose-600"
                  title={run.errorMessage}
                >
                  {run.errorMessage}
                </div>
              )}
            </TableCell>
            <TableCell>
              <SyncKindBadge kind={run.kind} />
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {run.upserted}
            </TableCell>
            <TableCell className="text-right tabular-nums text-slate-500">
              {run.kind === 'lines' ? run.softDeleted : '—'}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {run.errors > 0 ? (
                <span className="font-medium text-rose-600">{run.errors}</span>
              ) : (
                <span className="text-slate-400">0</span>
              )}
            </TableCell>
            <TableCell className="text-right tabular-nums text-slate-500">
              {formatDuration(run.durationMs)}
            </TableCell>
            <TableCell>
              <SyncStatusBadge status={run.status} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function NightRow({ night }: { night: NightGroup }) {
  const summaryParts = [
    `${night.orgCount} org${night.orgCount > 1 ? 's' : ''}`,
    `${night.linesUpserted} ligne${night.linesUpserted > 1 ? 's' : ''}`,
    `${night.sitesUpserted} site${night.sitesUpserted > 1 ? 's' : ''} maj`,
  ];

  return (
    <Collapsible>
      <CollapsibleTrigger className="group flex w-full items-center gap-3 py-3 text-left hover:bg-slate-50">
        <ChevronRight className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-data-[state=open]:rotate-90" />
        <MoonStar className="h-4 w-4 shrink-0 text-slate-400" />
        <span className="min-w-0 flex-1">
          <span className="text-sm font-medium text-slate-900">
            {formatNightLabel(night.startedAt)}
          </span>
          <span className="ml-2 text-xs text-slate-500">
            {summaryParts.join(' · ')}
          </span>
        </span>
        <span className="shrink-0 text-xs text-slate-400">
          {formatRelative(night.startedAt)}
        </span>
        {night.failures > 0 ? (
          <Badge className="shrink-0 bg-rose-100 text-rose-800 hover:bg-rose-100">
            {night.failures} échec{night.failures > 1 ? 's' : ''}
          </Badge>
        ) : (
          <Badge className="shrink-0 bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
            OK
          </Badge>
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="pb-3 pl-7">
        <RunsTable runs={night.runs} />
      </CollapsibleContent>
    </Collapsible>
  );
}

function SingleRunRow({ run }: { run: SyncRun }) {
  return (
    <div className="flex items-center gap-3 py-3">
      <span className="w-4 shrink-0" aria-hidden />
      <Badge variant="outline" className="shrink-0 text-xs">
        {scopeLabel(run.scope)}
      </Badge>
      <div className="min-w-0 flex-1">
        <span className="text-sm font-medium text-slate-900">
          {run.organizationName ?? 'Toutes les organisations'}
        </span>
        <span className="ml-2 text-xs text-slate-500">
          {run.upserted} maj
          {run.kind === 'lines' ? ` · ${run.softDeleted} soft-delete` : ''}
          {run.errors > 0 ? ` · ${run.errors} erreur(s)` : ''}
          {` · ${formatDuration(run.durationMs)}`}
        </span>
        {run.errorMessage && (
          <p
            className="max-w-md truncate text-xs text-rose-600"
            title={run.errorMessage}
          >
            {run.errorMessage}
          </p>
        )}
      </div>
      <span
        className="shrink-0 text-xs text-slate-400"
        title={formatExact(run.startedAt)}
      >
        {formatRelative(run.startedAt)}
      </span>
      <SyncKindBadge kind={run.kind} />
      <SyncStatusBadge status={run.status} />
    </div>
  );
}

interface SyncHistorySectionProps {
  items: HistoryItem[];
  loading: boolean;
  error: boolean;
}

export function SyncHistorySection({
  items,
  loading,
  error,
}: SyncHistorySectionProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Exécutions récentes</CardTitle>
        <p className="text-xs text-slate-500">
          Les syncs nocturnes sont regroupés par nuit ; les déclenchements
          manuels et initiaux sont listés individuellement.
        </p>
      </CardHeader>
      <CardContent>
        {loading && (
          <p className="py-8 text-center text-sm text-slate-400">Chargement…</p>
        )}
        {error && (
          <p className="py-8 text-center text-sm text-rose-600">
            Erreur lors du chargement de l'historique.
          </p>
        )}
        {!loading && !error && items.length === 0 && (
          <p className="py-8 text-center text-sm text-slate-400">
            Aucun sync exécuté. L'historique se remplira au prochain
            déclenchement (manuel ou cron).
          </p>
        )}
        {!loading && !error && items.length > 0 && (
          <div className="divide-y divide-slate-100">
            {items.map((item) =>
              item.type === 'night' ? (
                <NightRow key={`night-${item.key}`} night={item} />
              ) : (
                <SingleRunRow key={item.run.id} run={item.run} />
              )
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
